import { createContext, useContext } from 'react';
import {
  WebSocketEventType,
  ClientMessage,
  SubscriptionScope,
  EventHandler,
  MessageHandler,
  RawMessageHandler,
} from '../types/websocket';

/**
 * WebSocket Context Type
 *
 * Provides access to WebSocket connection state and methods for:
 * - Subscribing to events (regions, divisions, devices)
 * - Registering event handlers
 * - Sending messages to the backend
 */
interface WebSocketContextType {
  isConnected: boolean;

  /**
   * Send a message to the WebSocket server
   */
  sendMessage: (message: ClientMessage) => void;

  /**
   * Subscribe to events for a specific scope
   * Example: subscribe({ type: 'region', regionId: 'uuid' })
   */
  subscribe: (scope: SubscriptionScope) => void;

  /**
   * Unsubscribe from events for a specific scope
   */
  unsubscribe: (scope: SubscriptionScope) => void;

  /**
   * Register a handler for a specific event type
   * Returns an unsubscribe function
   *
   * Example:
   * ```
   * const unsubscribe = addEventListener(
   *   WebSocketEventType.DEVICE_STATUS_CHANGED,
   *   (payload) => {
   *     console.log('Device status changed:', payload);
   *     // Invalidate React Query cache or fetch new data
   *   }
   * );
   * ```
   */
  addEventListener: <T = unknown>(
    eventType: WebSocketEventType,
    handler: EventHandler<T>
  ) => () => void;

  /**
   * Register a global handler that receives all WebSocket messages
   * Useful for logging, analytics, or debugging
   * Returns an unsubscribe function
   */
  addGlobalListener: (handler: MessageHandler) => () => void;

  /**
   * Register a raw message handler that processes messages before the standard pipeline.
   * If any handler returns true, normal WebSocket processing is skipped.
   * Returns an unsubscribe function.
   */
  addRawListener: (handler: RawMessageHandler) => () => void;

  /**
   * Send a raw JSON message to the WebSocket server (for proxy data, etc.)
   */
  sendRaw: (message: Record<string, unknown>) => void;
}

// Create the context
export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * Hook to access WebSocket context
 * Must be used within a WebSocketProvider
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
